import { Card, Form, Row, Col, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import BasicTemplate from 'components/Toast/BasicTemplate';
import { departments } from '../../../../../data/TempData';
import UserDropdownV2 from 'components/dropdowns/CustomerServices/UserDropdownV2';
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import DepartmentDropdown from 'components/dropdowns/CustomerServices/DepartmentDropdown';
import PriorityDropdown from 'components/dropdowns/CustomerServices/PriorityDropdown';
import ProgressDropdown from 'components/dropdowns/CustomerServices/ProgressDropdown';
import InputDatePicker from './InputDatePicker';
import { getItemFromStore } from 'helpers/utils';
import { joinDataMultiDropdown } from 'helpers/dropdownFuncHelper';
import {
  getActionPlan,
  patchActionPlan
} from 'services/customer-service/scActionPlanService';

import TagsInput from 'react-tagsinput';
import 'react-tagsinput/react-tagsinput.css';

const mapDateOnlyToUtcDate = value => {
  if (!value) {
    return undefined;
  }

  const parsedDate = new Date(value);
  return new Date(
    Date.UTC(
      parsedDate.getFullYear(),
      parsedDate.getMonth(),
      parsedDate.getDate()
    )
  );
};

const mapChangedFieldToRequestValue = (name, value) => {
  switch (name) {
    case 'targetDate':
      return value
        ? new Date(
            Date.UTC(value.getFullYear(), value.getMonth(), value.getDate())
          )
        : undefined;
    case 'progress':
      return value?.value || 1;
    case 'reporter':
    case 'priority':
      return value?.value;
    case 'assignees':
      return value?.value ? `${value.value}` : '';
    case 'requestParticipants':
    case 'departments':
      return joinDataMultiDropdown(value ?? []);
    default:
      return value;
  }
};

const ActionPlanRightInfo = ({ id }) => {
  const currentUser = getItemFromStore('user', {}, sessionStorage);
  const [tags, setTags] = useState([]);
  const [isLoadingActionPlan, setIsLoadingActionPlan] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const defaultValues = {
    id: 0,
    progress: 0,
    priority: 1,
    reporter: 0,
    relativeJobNumbers: '',
    assignees: [],
    requestParticipants: [],
    departments: [],
    targetDate: new Date()
  };

  const [formData, setFormData] = useState(defaultValues);

  const handleChangeTag = newTags => {
    const uniqueTags = Array.from(new Set(newTags));
    setTags(uniqueTags);
    handleChange('relativeJobNumbers', uniqueTags.join(','));
  };

  useEffect(() => {
    const relatedJobTags = formData?.relativeJobNumbers
      ?.split(',')
      .filter(tag => tag.trim() !== '');
    console.log(relatedJobTags);
    if (relatedJobTags?.length > 0) {
      setTags(relatedJobTags);
    } else {
      setTags([]);
    }
  }, [formData]);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = () => {
    setIsLoadingActionPlan(true);
    getActionPlan(id)
      .then(res => {
        if (res?.data && res?.data.id > 0) {
          const mapData = mapRSData(res?.data);
          setFormData(mapData);
        }
      })
      .finally(() => setIsLoadingActionPlan(false));
  };

  const mapRSData = data => {
    if (data) {
      let mapRs = { ...data };
      if (data.targetDate) {
        mapRs.targetDate = mapDateOnlyToUtcDate(data.targetDate);
      }
      return mapRs;
    }
    return defaultValues;
  };

  const handleChange = async (name, value) => {
    const previousValue = formData[name];
    setFormData(current => ({ ...current, [name]: value }));

    const requestValue = mapChangedFieldToRequestValue(name, value);
    if (requestValue === undefined) return;

    setIsSaving(true);
    const result = await patchActionPlan({ [name]: requestValue }, id);
    setIsSaving(false);

    if (!result?.success) {
      setFormData(current => ({ ...current, [name]: previousValue }));
      toast.error(
        <BasicTemplate
          success={false}
          text={result?.message ?? 'There was a problem saving the Action Plan.'}
        />
      );
    }
  };

  return (
    <Card>
      <Card.Header as="h5">
        Other Info
        {(isLoadingActionPlan || isSaving) && (
          <Spinner
            animation="border"
            size="sm"
            role="status"
            style={{ marginLeft: '8px' }}
          />
        )}
      </Card.Header>
      <Card.Body className="bg-light">
        <Row>
          <Col md="6">
            <InputDatePicker
              column="targetDate"
              displayName="Target Date"
              selected={formData?.targetDate}
              handleChange={handleChange}
            />
          </Col>
          <Col md="6">
            <ProgressDropdown
              label="Status"
              fieldName="progress"
              isMulti={false}
              placeholder="Status..."
              handleChange={handleChange}
              value={formData?.progress ? formData?.progress : 1}
            />
          </Col>
        </Row>
        <Row>
          <Col md="6">
            <UserDropdownV2
              label="Assignee"
              fieldName="assignees"
              handleChange={handleChange}
              placeholder="Assignee..."
              value={formData?.assignees}
              isMulti={false}
              isClearable
            />
            <small className="text-muted d-block" style={{ marginTop: 4 }}>
              Only one person can be assigned. To add more people, use
              Request participants or create a sub task.
            </small>
          </Col>
          <Col md="6">
            <UserDropdownV2
              label="Request participants"
              fieldName="requestParticipants"
              handleChange={handleChange}
              placeholder="Request participants..."
              value={formData?.requestParticipants}
            />
          </Col>
        </Row>
        <Row>
          <Col md="6">
            <UserDropdownV2
              label="Reporter"
              fieldName="reporter"
              handleChange={handleChange}
              placeholder="Reporter..."
              value={
                formData?.reporter ? formData?.reporter : currentUser.userId
              }
              isMulti={false}
            />
          </Col>
          <Col md="6">
            <DepartmentDropdown
              label="Departments"
              fieldName="departments"
              closeMenuOnSelect={false}
              isMulti
              options={departments}
              placeholder="Department"
              handleChange={handleChange}
              value={formData?.departments}
            />
          </Col>
        </Row>
        <Row style={{ display: 'flex', flexWrap: 'wrap' }}>
          <Col md="6" style={{ display: 'flex', flexDirection: 'column' }}>
            <Form.Group controlId="relativeJobNumbers">
              <Form.Label>Relative Job Numbers</Form.Label>
              <TagsInput
                inputProps={{ placeholder: 'Relative Job Numbers' }}
                value={tags}
                onChange={handleChangeTag}
              />
            </Form.Group>
          </Col>
          <Col md="6" style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{ flex: 1 }}>
              <PriorityDropdown
                label="Priority"
                fieldName="priority"
                closeMenuOnSelect={false}
                isMulti={false}
                placeholder="Priority ..."
                handleChange={handleChange}
                value={formData?.priority ? formData?.priority : 0}
              />
            </div>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};
ActionPlanRightInfo.propTypes = {
  id: PropTypes.number,
  handleChange: PropTypes.func
};

export default ActionPlanRightInfo;
